import { useCallback, useEffect, useRef, useState } from 'react';
import { stcBinService } from '../../../../services/stcBinService';
import { LocationOverviewDto } from '../../../../types/locationOverview';

// Søket henter oversikten for å vite om lokasjonen finnes, og visningen åpner rett etterpå.
// Del ett kall per lager/lokasjon i et kort vindu (også mot StrictMode-dobbeltkjøring). reload() går alltid forbi.
const SHARE_WINDOW_MS = 3000;
const sharedRequests = new Map<string, { promise: Promise<LocationOverviewDto | null>; at: number }>();

export const fetchLocationOverview = (warehouseId: string, binCode: string, force = false) => {
  const key = `${warehouseId}|${binCode.toLowerCase()}`;
  const existing = sharedRequests.get(key);
  if (!force && existing && Date.now() - existing.at < SHARE_WINDOW_MS) {
    return existing.promise;
  }
  const promise = stcBinService.getLocationOverview(warehouseId, binCode);
  sharedRequests.set(key, { promise, at: Date.now() });
  promise.catch(() => sharedRequests.delete(key));
  return promise;
};

/**
 * Laster lokasjonsoppslaget (GET stc-bins/current/{warehouseId}/{binCode}/overview). `notFound` skiller
 * «finnes ikke i lageret» fra nettverks-/serverfeil. Svar på en gammel lokasjon forkastes ved bytte.
 */
export function useLocationOverview(warehouseId: string | null, binCode: string | null) {
  const [overview, setOverview] = useState<LocationOverviewDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const requestRef = useRef(0);

  const load = useCallback(async (force = false) => {
    if (!warehouseId || !binCode) {
      setOverview(null);
      return;
    }
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const result = await fetchLocationOverview(warehouseId, binCode, force);
      if (requestId !== requestRef.current) return;
      setOverview(result);
      setNotFound(result === null);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      console.error('Error loading location overview:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [warehouseId, binCode]);

  useEffect(() => {
    void load();
  }, [load]);

  const reload = useCallback(() => load(true), [load]);

  return { overview, loading, error, notFound, reload };
}
